import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Mail, Linkedin, Github, ArrowRight, Loader2, Check, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { profile } from '@/data/profile'
import { TURNSTILE_SITE_KEY } from '@/config/turnstile'

// Declare Turnstile types
declare global {
  interface Window {
    turnstile: {
      getResponse: () => string
      reset: () => void
    }
  }
}

const getContactMethods = () => [
  {
    title: 'Email',
    description: 'Get in touch directly',
    value: profile.email,
    icon: <Mail className="h-6 w-6" />,
    href: `mailto:${profile.email}`
  },
  {
    title: 'LinkedIn',
    description: 'Professional network',
    value: profile.linkedin.replace('https://', ''),
    icon: <Linkedin className="h-6 w-6" />,
    href: profile.linkedin
  },
  {
    title: 'GitHub',
    description: 'View my code',
    value: profile.github.replace('https://', ''),
    icon: <Github className="h-6 w-6" />,
    href: profile.github
  }
]

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  // Load Turnstile script
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    document.head.appendChild(script)

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]')
      if (existingScript) {
        document.head.removeChild(existingScript)
      }
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (submitStatus === 'error') {
      setSubmitStatus('idle')
      setErrorMessage('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')
    
    try {
      // Get Turnstile token
      const token = window.turnstile?.getResponse()
      console.log('Turnstile token:', token) // Debug log
      if (!token) {
        throw new Error('Please complete the security verification')
      }

      // Submit to backend
      const response = await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          token
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message')
      }

      // Success
      setFormData({ name: '', email: '', message: '' })
      setSubmitStatus('success')
      
      // Reset Turnstile
      window.turnstile?.reset()
      
    } catch (error) {
      setSubmitStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
      // Reset status after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle')
        setErrorMessage('')
      }, 5000)
    }
  }

  return (
    <section id="contact" className="py-20">
      <div className="container-max section-padding">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          {/* Section header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-text-light dark:text-text-dark mb-4">
              Get In Touch
            </h2>
            <p className="text-lg text-text-light/70 dark:text-text-dark/70 max-w-2xl mx-auto">
              Ready to collaborate? Let's discuss your next project or just say hello!
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact methods */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div>
                <h3 className="text-2xl font-semibold text-text-light dark:text-text-dark mb-4">
                  Let's Connect
                </h3>
                <p className="text-text-light/70 dark:text-text-dark/70 mb-8">
                  I'm always interested in new opportunities and collaborations. 
                  Whether you have a project in mind or just want to chat about 
                  technology and finance, feel free to reach out!
                </p>
              </div>

              <div className="space-y-4">
                {getContactMethods().map((method, index) => (
                  <motion.a
                    key={method.title}
                    href={method.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="block group"
                  >
                    <Card className="group-hover:card-hover transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                          <div className="text-text-light/60 dark:text-text-dark/60 group-hover:text-accent transition-colors">
                            {method.icon}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-text-light dark:text-text-dark group-hover:text-accent transition-colors">
                              {method.title}
                            </h4>
                            <p className="text-sm text-text-light/70 dark:text-text-dark/70 mb-1">
                              {method.description}
                            </p>
                            <p className="text-sm text-accent font-medium">
                              {method.value}
                            </p>
                          </div>
                          <ArrowRight className="h-4 w-4 text-text-light/40 dark:text-text-dark/40 group-hover:text-accent transition-colors" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Contact form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">Send a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-text-light dark:text-text-dark mb-2">
                          Name
                        </label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your name"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-text-light dark:text-text-dark mb-2">
                          Email
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-text-light dark:text-text-dark mb-2">
                        Message
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell me about your project or just say hello!"
                        rows={5}
                        required
                      />
                    </div>

                    {/* Cloudflare Turnstile */}
                    <div className="flex justify-center">
                      <div 
                        className="cf-turnstile" 
                        data-sitekey={TURNSTILE_SITE_KEY}
                        data-theme="auto"
                      ></div>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin -ml-1 mr-3 h-4 w-4" />
                          Sending...
                        </>
                      ) : submitStatus === 'success' ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Message Sent!
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </Button>

                    {submitStatus === 'success' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-center space-x-2 text-sm text-green-600 dark:text-green-400"
                      >
                        <Check className="h-4 w-4" />
                        <span>Message sent successfully! I'll get back to you soon.</span>
                      </motion.div>
                    )}
                    
                    {submitStatus === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-center space-x-2 text-sm text-red-600 dark:text-red-400"
                      >
                        <AlertCircle className="h-4 w-4" />
                        <span>{errorMessage}</span>
                      </motion.div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
